import styled, { createGlobalStyle, css } from "styled-components";

import { SystemIcon } from "@storeblocks/icon";
import StbSurface from "@storeblocks/surface";
import { Alert } from "Components/Form/Alert";
import { Props as CustomAlertProps } from "Components/Form/Alert/Alert";
import { isOfflineMode } from "Utils/isOfflineMode";

export const responsiveParagraph = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.xs};

  @media (min-width: 768px) {
    font-size: ${({ theme }) => theme.fontSizes.s};
  }
`;

export const GlobalStyle = createGlobalStyle`
  @layer  prezel-assets, legacy-assets;

  :root {
    --color-primary: var(--p-color-text-primary);
    --color-surface: var(--p-color-surface-1);
    --color-surface-border: var(--p-color-surface-1);
    --color-surface-variant-border: var(--p-color-surface-1);
    --color-on-background: var(--p-color-text-primary);
    --color-selection: var(--p-color-text-primary);
    --color-error-background: var(--p-color-surface-error);
    --color-error-accent: var(--p-color-icon-error);
    --footer-bottom-background: #fff !important;
    --footer-bottom-color: var(--p-color-text-primary) !important;
    --footer-link-color: var(--p-color-text-interactive) !important;
    --footer-top-background: #fff0f0 !important;
    --footer-top-color: var(--p-color-text-primary) !important;
  }

  html {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 100%;
  }

  * {
    box-sizing: border-box;

    /* font-family: "Open sans", sans-serif; */
    font-family: "Storebrand Text", sans-serif;
    margin: 0;
    overflow-wrap: anywhere;
    padding: 0;
  }

  a {
    /* color: ${({ theme }) => theme.link ?? "#191919"}; */

    /* color: ${({ theme }) =>
      theme.link ?? "var(--p-color-text-interactive)"}; */
    color: var(--p-color-text-interactive);

    &.button {
      color: var(--p-color-text-interactive)!important;
    }
  }

  p {
    ${responsiveParagraph}
    a {
      color: var(--p-color-text-interactive);
    }
  }

  input {
    color: var(--p-color-text-primary)
  }

  body.using-mouse :focus {
    box-shadow: none;
    outline: none;
  }

  body.using-mouse :focus-within {
    box-shadow: none;
    outline: none;
  }

  body {
    background-color:  ${({ theme }: any) =>
      theme.brandColors.background ?? "#fff"};
    display: flex;
    flex: 1 0 auto;
    flex-direction: column;
    height: 100%;
    padding-top: 0;

    & > #root {
      display: flex;
      flex: 1 0 auto;
      flex-direction: column;
    }
  }

  #react-refresh-overlay {
    display: ${isOfflineMode() ? "none" : "block"};
  }

  .dialog {
    ${({ theme }) => `
      --color-primary: ${theme.buttons.color};
      --color-primary-pressed: ${theme.buttons.pressed};
      --color-primary-variant: ${theme.buttons.hover};
    `}
  }

  .hotjar-survey-border > div {
    border: 2px solid var(--color-surface-border);
  }

  .hotjar-survey-no-shadow ._hj-widget-container ._hj-5vKq2__styles__surveyContainer._hj-7Itnd__styles__inline{
    box-shadow: none !important;
  }

  #ssx-check-price-survey {
    display: flex;
    justify-content: flex-start;
  }

  .with-red-link a {
    /* color: var(--color-primary); */
    color: var(--p-color-text-interactive);
  }

  /* Temp Hotjar unmasking
  .data-hj-suppress {
    color: red;
    border: 10px solid purple;
  } */
`;

export const Main = styled.main`
  align-items: center;
  background-color: inherit;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  margin: ${({ theme }) => theme.spacing.sm} auto;
  padding: 0 ${({ theme }) => theme.spacing.sm};
  width: 100%;

  @media screen and (min-width: 768px) {
    margin: 32px auto;
    max-width: 1176px;
    padding: 0 32px;
  }
`;

export const MainFlexbox = styled(Main)`
  display: flex;
  flex-direction: column;
`;

//TYPOGRAPHY-----------------------------------------------------------
export const Heading = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05, 4px);
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding-top: 8px;

  h1,
  h2,
  h3,
  h4,
  p {
    margin: 0;
  }
`;

export const H1 = styled.h1`
  color: ${({ theme }) => theme.headingColor ?? "#000000"};
  font-family: SignaWeb-ColumnBook, sans-serif;
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: ${({ theme }) => theme.lineHeight.xl};
  text-align: start;
`;

export const StbH1 = styled(H1)`
  color: ${({ theme }) => theme.brandColors.primary};
`;

export const H2 = styled.h2`
  color: ${({ theme }) => theme.headingColor ?? "#000000"};
  font-family: SignaWeb-ColumnBook, sans-serif;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: ${({ theme }) => theme.lineHeight.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  text-align: start;

  & + .ingress {
    margin-bottom: 0;
  }
`;

export const H3 = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-style: bold;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  line-height: ${({ theme }) => theme.lineHeight.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  text-align: start;

  & + .ingress {
    margin-bottom: 0;
  }
`;

export const H4 = styled.h4`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  line-height: ${({ theme }) => theme.lineHeight.md};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

export const Paragraph = styled.p`
  color: var(--p-color-text-primary);
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: ${({ theme }) => theme.lineHeight.md};
  text-align: start;

  & + * {
    margin-top: ${({ theme }) => theme.spacing.md};
  }
`;

export const BrandedParagraph = styled.p`
  background-color: ${({ theme }) => theme.brandedParagraph.backgroundColor};
  color: ${({ theme }) => theme.brandedParagraph.textColor};
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: ${({ theme }) => theme.lineHeight.md};
  margin: ${({ theme }) => theme.spacing.md} 0;
  padding: ${({ theme }) => theme.spacing.md};
  text-align: start;
`;

export const ParagraphAsHeading = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeight.extraBold};
  line-height: ${({ theme }) => theme.lineHeight.md};
`;

export const ParagraphAsLabel = styled.p`
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

export const ParagraphAsHint = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  margin-top: 0;
`;

export const LinkAsHint = styled.a`
  color: var(--p-color-text-interactive);
  font-size: ${({ theme }) => theme.fontSizes.xs};
`;

export const MediumText = styled.span`
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

export const BoldText = styled.span`
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

export const DescriptionList = styled.dl`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

export const Term = styled.dt`
  display: inline;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

export const Description = styled.dd`
  display: inline;
  margin-left: 4px;
`;

export const Ingress = styled.p`
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
`;

export const ParagraphLong = styled.div`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
`;
//DIVIDERS-----------------------------------------------------------------

export const Divider = styled.hr`
  background: rgb(25 25 25 / 50%);
  height: 1px;
  margin-top: 5px;
  opacity: 0.3;
  width: 100%;
`;

export const MainHeaderDivider = styled(Divider)`
  border-top: ${({ theme }) => theme.divider?.border ?? "1px"};
  opacity: ${({ theme }) => theme.divider?.opacity ?? 0.3};
`;

export const DividerMargin = styled(Divider)`
  height: 1;
  margin-bottom: 1rem;
  margin-top: 1rem;
`;

//ALERTS-------------------------------------------------------------------
export type AlertMargin = "0px" | "16px" | "24px" | "32px";

interface AlertProps extends CustomAlertProps {
  marginTop?: AlertMargin;
  marginBottom?: AlertMargin;
  transparent?: boolean;
}

export const StyledAlert = styled(Alert)<AlertProps>`
  margin-bottom: ${({ marginBottom, theme }) =>
    marginBottom ? marginBottom : theme.spacing.lg};
  margin-top: ${({ marginTop }) => (marginTop ? marginTop : "0px")};

  ${({ transparent }) => {
    if (transparent) {
      return `
        background-color: transparent;
        border-color: transparent;
        outline-color: transparent;
      `;
    }
    return ``;
  }}
`;

//WRAPPERS-----------------------------------------------------------------
export const WrapLgMarginBottom = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

export const WrapLgMarginTop = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

export const WrapMdMarginBottom = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const WrapSmMarginBottom = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

export const MainHeaderWrapper = styled.div`
  align-items: start;
  display: flex;
  flex-direction: column;
  padding: 0 ${({ theme }) => theme.spacing.sm};
  width: 100%;

  @media screen and (min-width: 768px) {
    padding: 0 32px;
  }

  @media screen and (max-width: 768px) {
    padding: 0;
  }
`;

export const LeftAlignedWrapper = styled.div`
  align-items: start;
  background-color: inherit;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  margin: ${({ theme }) => theme.spacing.sm} auto;
  padding: 0 ${({ theme }) => theme.spacing.sm};
  width: 100%;

  @media screen and (min-width: 768px) {
    margin: 32px auto;
    max-width: 1176px;
    padding: 0 32px;
  }

  @media screen and (max-width: 768px) {
    padding: 0;
  }
`;

interface StyledWrapProps {
  maxWidth?: string;
  padding?: string;
}

export const AnimatedWrap = styled.section<StyledWrapProps>`
  background-color: ${({ theme }) => theme.brandColors.background ?? "#fff"};
  border-radius: ${({ theme }) => theme.borderRadius.smallRounding};
  box-sizing: border-box;

  /* color: black; */
  color: var(--p-color-text-primary);
  justify-content: space-between;
  line-height: ${({ theme }) => theme.lineHeight.md};
  margin-bottom: 10px;
  margin-top: ${({ theme }) => theme.spacing.sm};
  max-width: ${({ maxWidth }) => maxWidth ?? "464px"};
  padding: ${({ padding }) => padding ?? "0"};
  transition: background-color 0.2s linear;
  width: 100%;
`;

export const ThemedBorderSurface = styled(StbSurface)`
  border: 2px solid ${({ theme }) => theme.surface.border};
`;

export const ThemedSurface = styled(StbSurface)`
  background: ${({ theme }) => theme.surface.color};
`;

export const PaddedSurface = styled(ThemedSurface)`
  display: block;
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.md}`};

  & + * {
    margin-top: ${({ theme }) => theme.spacing.lg};
  }
`;

export const FilledBorderSurface = styled(ThemedBorderSurface)`
  background: ${({ theme }) => theme.brandColors.surface.border};
`;

export const StyledDiv = styled.div`
  margin-bottom: 32px;
`;

export const StyledRow = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  @media screen and (max-width: 425px) {
    .svg {
      display: none !important;
    }
  }
`;

export const ButtonsWrap = styled.section`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;

  & .NextButton {
    order: 2;
  }

  & .PreviousButton {
    margin-right: 8px;
    order: 1;
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
`;

export const LinkWrap = styled.div`
  a {
    /* color: ${({ theme }) => theme.brandColors.primary}; */
    color: var(--p-color-text-interactive);
  }
`;

export const MarkdownWrap = styled.div`
  & > p {
    ${responsiveParagraph}
  }

  & > ul {
    font-size: ${({ theme }) => theme.fontSizes.sm};
    line-height: 24px;
    margin: 16px 0 16px 24px;
  }
`;

//ICONS-----------------------------------------------------------------------------

interface IconProps {
  addMargin?: "left" | "right";
}

export const StyledSystemIcon = styled(SystemIcon)<IconProps>`
  ${({ addMargin, theme }) => `

    margin-left: ${addMargin === "left" && theme.spacing.xs};
    margin-right: ${addMargin === "right" && theme.spacing.xs};
  `}
`;

export const WhiteSystemIcon = styled(StyledSystemIcon)`
  color: ${({ theme }) => theme.brandColors.icons.white};
`;

export const GreenSystemIcon = styled(StyledSystemIcon)`
  color: ${({ theme }) => theme.brandColors.icons.green};
`;

export const RedSystemIcon = styled(StyledSystemIcon)`
  color: ${({ theme }) => theme.brandColors.icons.red};
`;

export const OrangeSystemIcon = styled(StyledSystemIcon)`
  color: ${({ theme }) => theme.brandColors.icons.orange};
`;

/**
 * Web Component for recording JSON fixtures.
 */
export const RecordToJsonWrapper = styled.div`
  border: 4px dotted #ccc;
  display: flex;
  justify-content: center;
  margin: 1rem;
  padding: 0.5rem;
`;

//OVERLAYS-----------------------------------------------------------------------------

//Requires "position: relative" on parent
const OverlayStyle = css`
  ::after {
    content: "";
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }
`;

//Requires "position: relative" on parent
export const OverlayHoverDarkenStyle = css`
  ::after {
    background-color: var(--color-overlay-hover-darken);
  }
  ${OverlayStyle}
`;

//Requires "position: relative" on parent
export const OverlayPressedDarkenStyle = css`
  ::after {
    background-color: var(--color-overlay-pressed-darken);
  }
  ${OverlayStyle}
`;

export const WebDiscountParagraph = styled.p`
  font-weight: 600;
`;
